import { matchPath } from 'react-router-dom';
import { isMobileApp } from '@/utils/isMobileApp';
import type { ReportType } from '@/modules/reports/ReportTypes';

/**
 * Product analytics module taxonomy resolver — see
 * docs/product-analytics-contract.md §"Module taxonomy". Resolves a pathname
 * to a `module` (and, where known, a human `routeName` and the matched
 * route's `routePattern`) WITHOUT ever returning the pathname itself:
 * manager routes embed company/project slugs, and maquinista dynamic
 * segments embed ids, both of which must never leave this process as event
 * properties. The route pattern (`/machine/:machine_id`, `/reports/notices`)
 * is the de-slugged shape of the URL and is what `$pathname` /
 * `$current_url` carry (BIL-5257) so PostHog's URL-based reports show
 * screens instead of a single root.
 *
 * Pure and total: never throws, always returns at least `{ module: 'unknown' }`.
 *
 * IMPORTANT — this file must never import `@/modules/app/routes` (or
 * anything that transitively does). That module declares ~350
 * `React.lazy()` manager screens; importing it from here, which is reached
 * by shared chokepoints (SentryRoute.tsx, analyticsLink.ts) used by BOTH
 * apps, pulled the entire manager route tree into the maquinista build —
 * measured: dist/assets/*.js grew from 11,554,976 bytes (64 files) to
 * 15,114,624 bytes (416 files) building build-maquinista. Manager-route
 * resolution instead lives in managerTaxonomy.ts, a manager-only module
 * that registers itself here via `registerManagerModuleResolver` — see
 * that file and packages/frontend/src/manager.jsx.
 */

export type ModuleResolution = {
  module: string;
  routeName?: string;
  /**
   * The matched route's path pattern, router-relative and without the
   * manager's context prefix (`/:company?/:businessUnit?/:project?`):
   * `/reports/notices`, `/machine/:machine_id`. Param placeholders stay
   * as-is, so it carries no slug or id. Absent when the module is
   * `unknown`.
   */
  routePattern?: string;
};

/**
 * The closed list of product areas `module` can take (BIL-5331), shared by
 * both apps — `app` already tells manager from operario. Every manager
 * route declares one as `analyticsModule` (a required field, so TypeScript
 * and a coverage test enforce it); every maquinista route gets one from
 * MAQUINISTA_ROUTE_MAP below. `unknown` is NOT part of the list: it is only
 * the fallback for a path no route matches.
 */
export const ANALYTICS_MODULES = [
  'inicio',
  'equipos',
  'partes_diarios',
  'novedades',
  'checklists',
  'combustible',
  'ordenes_de_trabajo',
  'mantenimiento',
  'telemetria',
  'documentos',
  'rental',
  'proyectos',
  'usuarios',
  'cuenta',
  'administracion',
  'analitica',
] as const;

export type AnalyticsModule = (typeof ANALYTICS_MODULES)[number];

/** The closed-list check, shared by the taxonomy tests and the intent reader. */
export const isAnalyticsModule = (value: string): value is AnalyticsModule =>
  (ANALYTICS_MODULES as readonly string[]).includes(value);

// Manager-route resolution is registered at runtime by managerTaxonomy.ts
// (imported only from manager.jsx) so this shared module never depends on
// the manager route manifest. undefined in the maquinista build, where
// registerManagerModuleResolver is never called.
export type ModuleResolver = (
  pathname: string
) => { module: string; routeName?: string } | undefined;

let managerResolver: ModuleResolver | undefined;

export const registerManagerModuleResolver = (
  resolver: ModuleResolver
): void => {
  managerResolver = resolver;
};

// Maquinista routes are NOT driven by a manifest — declared inline in
// AppMaquinista.tsx and MachinistRouter.tsx. This hand map is the ONE
// hand-maintained exception to the manager manifest, mirrored in
// docs/product-analytics-contract.md §"Module taxonomy". Every entry
// declares its product area (`module`).
//
// WHOEVER ADDS OR RENAMES A MAQUINISTA ROUTE MUST UPDATE BOTH THIS MAP AND
// THE CONTRACT TABLE. taxonomy.test.ts asserts every entry's module is in
// ANALYTICS_MODULES; the contract table is guarded by this comment only.
export const MAQUINISTA_ROUTE_MAP: Array<{
  path: string;
  exact?: boolean;
  module: AnalyticsModule;
  routeName: string;
}> = [
  {
    path: '/create',
    exact: true,
    module: 'administracion',
    routeName: 'Crear compañía',
  },
  {
    path: '/(confirm-employee|notifications)',
    exact: true,
    module: 'cuenta',
    routeName: 'Notificaciones',
  },
  {
    path: '/settings',
    exact: true,
    module: 'cuenta',
    routeName: 'Configuración',
  },
  { path: '/profile', exact: true, module: 'cuenta', routeName: 'Perfil' },
  {
    path: '/identity-claim',
    exact: true,
    module: 'cuenta',
    routeName: 'Hacé tuya tu cuenta',
  },
  {
    path: '/QR/:version/',
    exact: true,
    module: 'administracion',
    routeName: 'QR handler',
  },
  {
    path: '/ot/:id?',
    module: 'ordenes_de_trabajo',
    routeName: 'Orden de trabajo',
  },
  {
    path: '/my-work-orders',
    module: 'ordenes_de_trabajo',
    routeName: 'Mis órdenes de trabajo',
  },
  { path: '/my-documents', module: 'documentos', routeName: 'Mis documentos' },
  {
    path: '/notifications',
    module: 'cuenta',
    routeName: 'Notificaciones',
  },
  { path: '/machine/:machine_id', module: 'equipos', routeName: 'Máquina' },
  { path: '/', exact: true, module: 'inicio', routeName: 'Dashboard' },
];

// The `/my-reports/:report_type[/new[/...]|/:report_id]` family: one URL
// shape for four product areas, told apart by `report_type`. The record is
// keyed on the `ReportType` enum values (type-only import, erased at
// runtime), so a new or renamed type does not compile until it has a row.
// Create / view screens render as modals routed under the list, so each
// type has a list, a new and a view name. Patterns carry no machine id or
// report id (BIL-5324).
const MY_REPORTS_LIST_PATTERN = '/my-reports/:report_type';
const MY_REPORTS_NEW_PATTERN = '/my-reports/:report_type/new';
const MY_REPORTS_VIEW_PATTERN = '/my-reports/:report_type/:report_id';

export const MY_REPORTS_BY_TYPE: Record<
  `${ReportType}`,
  { module: AnalyticsModule; list: string; create: string; view: string }
> = {
  DailyReport: {
    module: 'partes_diarios',
    list: 'Partes diarios',
    create: 'Nuevo parte diario',
    view: 'Ver parte diario',
  },
  MachineNotice: {
    module: 'novedades',
    list: 'Novedades',
    create: 'Nueva novedad',
    view: 'Ver novedad',
  },
  Checklist: {
    module: 'checklists',
    list: 'Checklists',
    create: 'Nuevo checklist',
    view: 'Ver checklist',
  },
  FuelReport: {
    module: 'combustible',
    list: 'Despachos',
    create: 'Nuevo despacho',
    view: 'Ver despacho',
  },
  StockReport: {
    module: 'combustible',
    list: 'Stock',
    create: 'Nuevo stock',
    view: 'Ver stock',
  },
};

const resolveMyReports = (pathname: string): ModuleResolution | undefined => {
  // Prefix match: covers `/new`, `/new/12` and the dispatch variants
  // `/new/source/12`, `/new/destination`.
  const created = matchPath<{ report_type: string }>(pathname, {
    path: MY_REPORTS_NEW_PATTERN,
    exact: false,
  });
  const viewed = created
    ? null
    : matchPath<{ report_type: string }>(pathname, {
        path: MY_REPORTS_VIEW_PATTERN,
        exact: true,
      });
  const listed =
    created || viewed
      ? null
      : matchPath<{ report_type?: string }>(pathname, {
          path: `${MY_REPORTS_LIST_PATTERN}?`,
          exact: false,
        });
  const match = created ?? viewed ?? listed;
  if (!match) return undefined;
  const reportType = match.params.report_type;
  const known =
    reportType && reportType in MY_REPORTS_BY_TYPE
      ? MY_REPORTS_BY_TYPE[reportType as `${ReportType}`]
      : undefined;
  // The pattern follows the matched shape even for an unknown type: the
  // before_send scrub re-resolves an already-scrubbed `$pathname`, whose
  // `:report_type` segment is not a known type, and must get the same
  // pattern back (resolveRoutePatternForPath is idempotent).
  const routePattern = created
    ? MY_REPORTS_NEW_PATTERN
    : viewed
      ? MY_REPORTS_VIEW_PATTERN
      : MY_REPORTS_LIST_PATTERN;
  // The bare list (`/my-reports`: deep links and the Partes tab's fallback;
  // MyReportsPage redirects from it to the first permitted type) counts as
  // daily reports. A type the record does not know is `unknown`, like any
  // unmatched path. The pattern is kept either way, for the scrub.
  if (!reportType) {
    return { module: 'partes_diarios', routeName: 'Mis partes', routePattern };
  }
  if (!known) return { module: 'unknown', routePattern };
  const routeName = created ? known.create : viewed ? known.view : known.list;
  return { module: known.module, routeName, routePattern };
};

const resolveMaquinistaModule = (pathname: string): ModuleResolution => {
  const myReports = resolveMyReports(pathname);
  if (myReports) return myReports;
  for (const entry of MAQUINISTA_ROUTE_MAP) {
    const match = matchPath(pathname, { path: entry.path, exact: entry.exact });
    if (match) {
      return {
        module: entry.module,
        routeName: entry.routeName,
        routePattern: toRoutePattern(entry.path),
      };
    }
  }
  return { module: 'unknown' };
};

/**
 * Exported for scrubUrlProperties.ts (backing posthog-js's `before_send`
 * hook in posthog.ts), which needs the same normalised basename to build
 * the scrubbed URL posthog-js's own URL/pathname/referrer properties get
 * replaced with. Keep this the single source of truth for basename
 * normalisation — do not duplicate the trailing-slash stripping elsewhere.
 */
export const getBasename = (): string => {
  const base = import.meta.env.BASE_URL || '/';
  return base === '/' ? '' : base.replace(/\/+$/, '');
};

/** Strips the app basename (see getBasename() in the entry files) from a
 * pathname, so route matching lines up with the manifest's router-relative
 * paths. A no-op when the app is served from the domain root. */
export const stripBasename = (pathname: string): string => {
  const basename = getBasename();
  if (!basename || !pathname.startsWith(basename)) return pathname;
  const stripped = pathname.slice(basename.length);
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
};

/**
 * The safe, URL-shaped stand-in for a concrete pathname: the matched
 * route's pattern, or `/` when nothing matches. Used by SentryRoute.tsx for
 * our own pageviews and by scrubUrlProperties.ts for every URL-like
 * property posthog-js stamps itself. Idempotent — a pattern resolves to
 * itself (its `:param` segments match the route's own params).
 */
export const resolveRoutePatternForPath = (pathname: string): string =>
  resolveModuleForPath(pathname).routePattern ?? '/';

/**
 * A route path as react-router declares it, made safe to embed in a URL:
 * the optional-param marker `?` (`/ot/:id?` → `/ot/:id`) would otherwise
 * read as a query string in `$current_url`. Placeholders (`:id`) and
 * literal segments are kept as-is.
 */
export const toRoutePattern = (path: string): string => path.replace(/\?/g, '');

/**
 * Both PWAs declare `"start_url": "./index.html"` in
 * packages/frontend/public/manifest*.json, so a launch from the home screen
 * (or a "home" link) lands on `/index.html`, a path no route knows. In
 * production that was 36% of the operator app's pageviews reported as
 * `module: 'unknown'` (replay confirmed
 * `start_url = https://operario.bilderit.com/index.html`). Treat it exactly
 * like `/`. Expects a basename-stripped pathname.
 */
const normalizePwaStartUrl = (relativePathname: string): string =>
  relativePathname === '/index.html' ? '/' : relativePathname;

/**
 * Resolves a pathname to a product-analytics module + route name (+ route
 * pattern). Never returns the pathname, a slug, or an id — only `module`,
 * `routeName` and the de-slugged `routePattern` may be emitted as event
 * properties. Never throws.
 */
export const resolveModuleForPath = (pathname: string): ModuleResolution => {
  try {
    const relativePathname = normalizePwaStartUrl(stripBasename(pathname));
    if (isMobileApp()) return resolveMaquinistaModule(relativePathname);
    return managerResolver?.(relativePathname) ?? { module: 'unknown' };
  } catch {
    return { module: 'unknown' };
  }
};

/**
 * `module` + `route_name` of the current screen, as event properties, from
 * one resolution so they can never disagree. `route_name` is omitted when
 * the path matches no route (module `unknown`).
 */
export const currentScreenProperties = (): {
  module: string;
  route_name?: string;
} => {
  const { module, routeName } = resolveModuleForPath(window.location.pathname);
  return routeName ? { module, route_name: routeName } : { module };
};
